import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import Select from 'react-select';
import DateCalendar from '../../../../components/calendars/DateCalendar/DateCalendar';
import { initiationTimeOptions } from '../../../../components/init-form/settings/submition-settings-frequency/constants';

// A setting that is rejected as a whole is reported on the setting itself, anything
// else per field -- and either may arrive as a bare string or wrapped in a list.
const firstMessage = (error) => (Array.isArray(error) ? error[0] : error);

// The hours are offered as `hh:mm A`, always on the hour, and are read back without a
// parsing plugin -- the app does not load one.
const getHourOfDay = (time) => {
  const [clock, meridiem] = time.split(' ');
  const hour = Number(clock.split(':')[0]) % 12;

  return meridiem === 'PM' ? hour + 12 : hour;
};

// Only an hour on today can already have passed; any later date leaves all of them open.
const isTimeInThePast = (option, date) => {
  if (!date || !dayjs(date).isSame(dayjs(), 'day')) {
    return false;
  }

  return dayjs(date).hour(getHourOfDay(option.value)).startOf('hour').isBefore(dayjs());
};

const TaskScheduledInitiationSettings = ({ formData, handleFormChange, errors }) => {
  const settingsError = typeof errors === 'string' || Array.isArray(errors) ? firstMessage(errors) : null;
  const getFieldError = (key) => (settingsError ? null : firstMessage(errors?.[key]));
  const scheduledInitiation = formData.scheduled_initiation || {};
  const scheduledDate = scheduledInitiation.date ?? null;

  const timeOptions = useMemo(() => initiationTimeOptions.filter((option) => !isTimeInThePast(option, scheduledDate)), [scheduledDate]);

  const handleScheduledInitiationChange = useCallback(
    (key, value) => handleFormChange(`scheduled_initiation.${key}`, value),
    [handleFormChange],
  );

  // Moving the initiation to another day can leave an hour selected that has since
  // become unavailable, so the time is dropped unless it survives the new date.
  const handleDateChange = useCallback(
    (date) => {
      handleFormChange('scheduled_initiation', {
        date,
        time: scheduledInitiation.time && !isTimeInThePast(scheduledInitiation.time, date) ? scheduledInitiation.time : null,
      });
    },
    [handleFormChange, scheduledInitiation.time],
  );

  return (
    <div className="form-control-item-settings">
      <div className="form-settings-label">
        <label className="form-label form-label-big">Does the task have a one-time initiation scheduled for a specific time?</label>
        <span>Define whether the forms of this task should be initiated once, at a date and time you choose, instead of right now.</span>
      </div>
      <div className="form-settings-inputs">
        <div className="d-flex">
          <div className="form-control-radio">
            <input
              name="will_have_scheduled_initiation"
              type="radio"
              checked={formData.will_have_scheduled_initiation === true}
              onChange={() => handleFormChange('will_have_scheduled_initiation', true)}
            />
            <span>Yes</span>
          </div>
          <div className="form-control-radio">
            <input
              name="will_have_scheduled_initiation"
              type="radio"
              checked={formData.will_have_scheduled_initiation === false}
              onChange={() => handleFormChange('will_have_scheduled_initiation', false)}
            />
            <span>No</span>
          </div>
        </div>
        {settingsError && <span className="form-error mb-2">{settingsError}</span>}

        {formData.will_have_scheduled_initiation && (
          <>
            <div className={'form-item form-item-select mb-4'} style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <label className="form-label">Initiation Date</label>
              <DateCalendar
                isClearable
                minDate={new Date()}
                selectedDate={scheduledDate}
                placeholderText="Choose Date"
                handleChange={handleDateChange}
              />
              {getFieldError('date') && <span className="form-error mt-1">{getFieldError('date')}</span>}
            </div>

            <div className={'form-item form-item-select mb-4'}>
              <label className="form-label">Initiation Time</label>
              <Select
                menuPlacement="top"
                options={timeOptions}
                className="form-control-select"
                classNamePrefix="react-select"
                isSearchable={false}
                value={scheduledInitiation.time ?? null}
                placeholder="Choose Time"
                onChange={(option) => handleScheduledInitiationChange('time', option)}
              />
              {getFieldError('time') && <span className="form-error mt-1">{getFieldError('time')}</span>}
              <span className={'text-secondary'}>
                The forms of the task will be initiated for submission once, on the selected date, at the selected time.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TaskScheduledInitiationSettings;
